import { defaultClone } from "../lib/clone"
import { normalizeConfigBase } from "../lib/config"
import { ConfigError, EvolutionError } from "../lib/errors"
import { validateConfig, validateProblem } from "../lib/validation"
import { mulberry32 } from "../lib/rng"
import type {
  GPConfig,
  GPMetrics,
  GPProblem,
  GPResult,
  OnGeneration,
  RNG,
  Selector,
  StopReason,
} from "../types"
import { tournament } from "./selection"
import { FitnessCache } from "../lib/fitness-cache"
import { computePopulationStats } from "../lib/stats"
import { buildRunMetrics } from "../lib/metrics"

/**
 * GPLiten: a minimal, zero-dependency genetic programming/algorithm engine.
 *
 * - Synchronous operations only (fitness/mutate/crossover are sync)
 * - Deterministic RNG via `mulberry32`
 * - Early stopping via `targetFitness` and `stall` window
 * - Optional problem hooks: `isValid`, `repair`
 */
type EngineSyncConfig<T> = ReturnType<typeof normalizeConfigBase<T>> & {
  rng: RNG
  selector: Selector<T>
  hooks: import("../types").GPHooks<T>
  clone: (g: Readonly<T>) => T
  cacheFitness: boolean
  fitnessKey?: (g: Readonly<T>) => string
}

/**
 * Synchronous GP/GA engine.
 *
 * Generation lifecycle (per t):
 * 1) Evaluate/repair initial population (t=0) or carry `pop` from previous gen.
 * 2) Compute stats and invoke hooks.
 * 3) Build next population `next`:
 *    - Copy `elite` best individuals unchanged (identity preserved).
 *    - Produce breeding children to fill up to `popSize - immigrantsPerGen`:
 *      - Allocate crossover child target from `cxProb`.
 *      - Fill the remainder with mutation children (since Pc + Pm = 1).
 *    - Append immigrants (sampled via createRandom) to reach `popSize`.
 * 4) Apply budgets/stop checks and repeat.
 *
 * Invariants:
 * - Pc + Pm = 1 (immigration is separate), E + M + N = λ each gen.
 * - Elites are copied without modification; identity is preserved across gens.
 * - Metrics counters reflect actual operations and evaluations.
 */
export class GPLite<T> {
  readonly cfg: EngineSyncConfig<T>
  readonly rng: RNG
  private select: Selector<T>
  // Fitness memoization helper
  private fitnessCache: FitnessCache<T>
  private problemSync: {
    createRandom(rng: RNG): T
    fitness(g: T): number
    mutate(g: T, rng: RNG): T
    crossover(a: T, b: T, rng: RNG): T[]
    isValid?(g: T): boolean
    repair?(g: T, rng: RNG): T
    distance?(a: T, b: T): number
  }

  constructor(
    private problem: GPProblem<T>,
    cfg: GPConfig<T> = {},
  ) {
    // Validate inputs for clearer developer feedback
    validateProblem(problem)
    validateConfig(cfg)
    const base = normalizeConfigBase(cfg)
    this.cfg = {
      ...base,
      rng:
        cfg.rng ??
        (cfg.seed !== undefined ? mulberry32(cfg.seed) : mulberry32()),
      selector: cfg.selector ?? tournament(base.tournament),
      hooks: cfg.hooks ?? {},
      clone: cfg.clone ?? defaultClone,
      cacheFitness: cfg.cacheFitness ?? false,
      fitnessKey: cfg.fitnessKey,
    }
    this.rng = this.cfg.rng
    this.select = this.cfg.selector
    // Setup optional fitness cache
    this.fitnessCache = new FitnessCache<T>({
      enabled: !!cfg.cacheFitness,
      fitnessKey: cfg.fitnessKey,
    })
    // Narrow to sync-only variant for the sync engine; runtime guards will enforce.
    this.problemSync = this.problem as unknown as GPLite<T>["problemSync"]
    if (this.cfg.elite > this.cfg.popSize) {
      throw new ConfigError(
        "Elite count cannot exceed population size",
        "E_CFG_ELITE_GT_POP",
      )
    }
    // allow tournament sizes larger than population (sampling with replacement)
  }

  static seed(seed: number) {
    return mulberry32(seed)
  }

  private assertNotPromise<TV>(what: string, v: TV): TV {
    if (v && typeof (v as unknown as { then?: unknown }).then === "function") {
      throw new EvolutionError(
        `${what} returned a Promise in sync engine. Use GPLiteAsync.run or make ${what} synchronous.`,
        "E_SYNC_PROMISE",
      )
    }
    return v
  }

  /** Guard fitness evaluation and invalid genomes. */
  private safeFitness(
    g: T,
    metrics?: Pick<
      GPMetrics,
      | "evaluations"
      | "invalidEvaluations"
      | "repaired"
      | "repairFailures"
      | "fitnessErrors"
      | "nonFiniteFitness"
    >,
  ): number {
    let genome = g
    if (metrics) {
      metrics.evaluations += 1 // count attempts even if served from cache
    }

    // Cache lookup (finite-only values are cached)
    {
      const got = this.fitnessCache.get(genome)
      if (got !== undefined) return got
    }

    // Validate and potentially repair
    if (this.problemSync.isValid && !this.problemSync.isValid(genome)) {
      if (this.problemSync.repair) {
        genome = this.problemSync.repair(genome, this.rng)
        this.assertNotPromise("repair", genome)
        // If still invalid after repair, assign worst fitness
        if (!this.problemSync.isValid(genome)) {
          if (metrics) {
            metrics.invalidEvaluations += 1
            metrics.repairFailures += 1
          }
          return -Infinity
        }
        if (metrics) metrics.repaired += 1
      } else {
        if (metrics) metrics.invalidEvaluations += 1
        return -Infinity
      }
    }

    // Evaluate fitness with safety checks
    try {
      const f = this.problemSync.fitness(genome)
      this.assertNotPromise("fitness", f)
      // Handle various edge cases
      if (!Number.isFinite(f) || f !== f) {
        if (metrics) {
          metrics.invalidEvaluations += 1
          metrics.nonFiniteFitness += 1
        }
        return -Infinity
      }
      // Cache finite values only
      this.fitnessCache.set(genome, f)
      return f
    } catch {
      // If fitness function throws, treat as invalid
      if (metrics) {
        metrics.invalidEvaluations += 1
        metrics.fitnessErrors += 1
      }
      return -Infinity
    }
  }

  /**
   * Execute the evolutionary loop (synchronous).
   *
   * Note on callbacks: `onGen` is a convenience alias to observe per-generation
   * progress. For most integrations, prefer the structured lifecycle hooks
   * provided via `new GPLite(problem, { hooks: { ... } })` — e.g.
   * `onGenerationStart`, `onGenerationEnd`, or `onIteration` — to keep the
   * problem definition pure and avoid coupling domain logic with progress I/O.
   */
  run(onGen?: OnGeneration<T>): GPResult<T> {
    const t0 = Date.now()
    // Track per-generation max timings for observability
    let lastStageMax: {
      init?: number
      evaluation?: number
      mutation?: number
      crossover?: number
    } = {}
    let stopReason: StopReason | undefined
    // metrics counters
    const metrics = {
      evaluations: 0,
      invalidEvaluations: 0,
      repaired: 0,
      repairFailures: 0,
      fitnessErrors: 0,
      nonFiniteFitness: 0,
      mutations: 0,
      crossovers: 0,
      selections: 0,
      immigrants: 0,
    }
    // constants derived from config
    const baseSlots = Math.max(0, this.cfg.popSize - this.cfg.elite)
    const immigrantsPerGen = Math.min(
      Math.round(baseSlots * this.cfg.immigration),
      baseSlots,
    )
    // 1. initialise population
    const initStart = Date.now()
    let pop: Array<{ g: T; f: number }> = Array.from(
      { length: this.cfg.popSize },
      () => {
        const g = this.problemSync.createRandom(this.rng)
        this.assertNotPromise("createRandom", g)
        return { g, f: this.safeFitness(g, metrics) }
      },
    )
    // Capture total initialization wall time for generation 0 reporting
    lastStageMax = { init: Date.now() - initStart }
    // budget check after initialisation
    if (metrics.evaluations >= this.cfg.maxEvaluations) {
      pop.sort((a, b) => b.f - a.f)
      return {
        bestGenome: pop[0].g,
        bestFitness: pop[0].f,
        generations: 0,
        bestFitnessHistory: [pop[0].f],
        bestGenomeHistory: [pop[0].g],
        elapsedMs: Date.now() - t0,
        stopReason: "evaluations",
        avgFitnessHistory: [-Infinity],
        invalidCountHistory: [0],
        validShareHistory: [1],
        metrics: buildRunMetrics(metrics, {
          popSize: this.cfg.popSize,
          generations: this.cfg.generations,
          cxProb: this.cfg.cxProb,
          mutProb: this.cfg.mutProb,
          crossoverChildren: this.cfg.crossoverChildren,
          immigration: this.cfg.immigration,
          tournament: this.cfg.tournament,
          stall: this.cfg.stall,
          targetFitness: this.cfg.targetFitness,
          maxWallMs: this.cfg.maxWallMs,
          maxEvaluations: this.cfg.maxEvaluations,
          elitesPerGen: this.cfg.elite,
        }),
      }
    }

    const history: number[] = []
    const meanHistory: number[] = []
    const invalidHistory: number[] = []
    const validShareHistory: number[] = []
    const bestGenomeHistory: T[] = []
    for (let gen = 0; gen < this.cfg.generations; ++gen) {
      // lifecycle hook: signal generation start
      this.cfg.hooks.onGenerationStart?.({
        generation: gen,
        popSize: this.cfg.popSize,
        elapsedMs: Date.now() - t0,
      })
      // rank by fitness
      pop.sort((a, b) => b.f - a.f)

      // basic stats
      const { best, mean, invalidCount, finiteCount } = computePopulationStats(
        pop,
      )
      history.push(best)
      bestGenomeHistory.push(pop[0].g)
      meanHistory.push(mean)
      invalidHistory.push(invalidCount)
      validShareHistory.push(finiteCount / pop.length)
      // Determine stop reason prior to emitting callbacks to surface it
      let nextStop: StopReason | undefined
      if (best >= this.cfg.targetFitness) nextStop = "target"
      else if (Date.now() - t0 >= this.cfg.maxWallMs) nextStop = "time"
      else if (metrics.evaluations >= this.cfg.maxEvaluations)
        nextStop = "evaluations"
      else {
        const w = this.cfg.stall
        if (
          w &&
          history.length > w &&
          history[history.length - 1] <= history[history.length - 1 - w]
        ) {
          nextStop = "stall"
        }
      }

      const payload = {
        generation: gen,
        bestFitness: best,
        avgFitness: mean,
        popSize: pop.length,
        invalidCount,
        validShare: finiteCount / pop.length,
        bestGenome: pop[0].g,
        elapsedMs: Date.now() - t0,
        stopReason: nextStop,
        stageMaxMs: lastStageMax,
      }
      onGen?.(payload)
      // lifecycle hook: after stats computed for the generation
      this.cfg.hooks.onGenerationEnd?.(payload)
      // alias progress hook
      this.cfg.hooks.onIteration?.(payload)

      if (nextStop) {
        stopReason = nextStop
        break
      }

      // 2. next population – start with elites
      const next: typeof pop = pop.slice(0, this.cfg.elite)

      // 3. breeding
      // Track maxima for work performed to produce the NEXT population
      const nextStageMax: {
        evaluation?: number
        mutation?: number
        crossover?: number
      } = {}
      const targetChildren = this.cfg.popSize - immigrantsPerGen
      {
        // Breeding allocation: allocate fixed child counts based on Pc (crossover)
        // and fill the remainder with mutation. Immigration is already reserved
        // via `targetChildren`, so we never subtract it again here.
        const slots = targetChildren - next.length
        const cxChildrenTarget = Math.min(
          slots,
          Math.round((this.cfg.popSize - this.cfg.elite) * this.cfg.cxProb),
        )
        const mutChildrenTarget = Math.max(0, slots - cxChildrenTarget)

        // Crossover children
        let produced = 0
        while (produced < cxChildrenTarget) {
          const p1 = this.select(pop, this.rng)
          const p2 = this.select(pop, this.rng)
          metrics.selections += 2
          const pa = this.cfg.clone(pop[p1].g)
          const pb = this.cfg.clone(pop[p2].g)
          const cxT0 = Date.now()
          const children = this.problemSync.crossover(pa, pb, this.rng)
          const cxDt = Date.now() - cxT0
          if (cxDt > (nextStageMax.crossover ?? 0))
            nextStageMax.crossover = cxDt

          this.assertNotPromise("crossover", children)
          metrics.crossovers += 1

          // Process each child from crossover
          const numToTake = Math.min(
            children.length,
            cxChildrenTarget - produced,
            targetChildren - next.length,
          )
          for (let i = 0; i < numToTake; i++) {
            let child = children[i]
            if (child === pa || child === pb) child = this.cfg.clone(child)

            const evalT0 = Date.now()
            const f = this.safeFitness(child, metrics)
            const evalDt = Date.now() - evalT0
            if (evalDt > (nextStageMax.evaluation ?? 0))
              nextStageMax.evaluation = evalDt
            next.push({ g: child, f })
            produced += 1

            if (metrics.evaluations >= this.cfg.maxEvaluations) {
              stopReason = "evaluations"
              break
            }
          }

          if (stopReason) break
        }
        if (stopReason) {
          pop = next
          break
        }

        // Mutation children
        for (
          let i = 0;
          i < mutChildrenTarget && next.length < targetChildren;
          i++
        ) {
          const p = this.select(pop, this.rng)
          metrics.selections += 1
          const before = this.cfg.clone(pop[p].g)
          const mutT0 = Date.now()
          let child = this.problemSync.mutate(before, this.rng)
          const mutDt = Date.now() - mutT0
          if (mutDt > (nextStageMax.mutation ?? 0))
            nextStageMax.mutation = mutDt
          this.assertNotPromise("mutate", child)
          if (child === before) child = this.cfg.clone(child)
          metrics.mutations += 1
          {
            const evalT0 = Date.now()
            const f = this.safeFitness(child, metrics)
            const evalDt = Date.now() - evalT0
            if (evalDt > (nextStageMax.evaluation ?? 0))
              nextStageMax.evaluation = evalDt
            next.push({ g: child, f })
          }
          if (metrics.evaluations >= this.cfg.maxEvaluations) {
            stopReason = "evaluations"
            break
          }
        }
        if (stopReason) {
          pop = next
          break
        }
      }
      if (stopReason) {
        pop = next
        break
      }

      // 4. immigrants
      for (let i = 0; i < immigrantsPerGen; ++i) {
        const g = this.problemSync.createRandom(this.rng)
        this.assertNotPromise("createRandom", g)
        {
          const evalT0 = Date.now()
          const f = this.safeFitness(g, metrics)
          const evalDt = Date.now() - evalT0
          if (evalDt > (nextStageMax.evaluation ?? 0))
            nextStageMax.evaluation = evalDt
          next.push({ g, f })
        }
        metrics.immigrants += 1
        if (metrics.evaluations >= this.cfg.maxEvaluations) {
          stopReason = "evaluations"
          break
        }
      }
      if (stopReason) {
        pop = next
        break
      }
      // Carry forward the maxima for reporting on the next iteration
      lastStageMax = {
        init: undefined,
        evaluation: nextStageMax.evaluation,
        mutation: nextStageMax.mutation,
        crossover: nextStageMax.crossover,
      }
      pop = next
    }

    pop.sort((a, b) => b.f - a.f)
    if (!stopReason) stopReason = "generations"
    return {
      bestGenome: pop[0].g,
      bestFitness: pop[0].f,
      generations: history.length,
      bestFitnessHistory: history,
      bestGenomeHistory,
      elapsedMs: Date.now() - t0,
      stopReason,
      avgFitnessHistory: meanHistory,
      invalidCountHistory: invalidHistory,
      validShareHistory,
      metrics: buildRunMetrics(metrics, {
        popSize: this.cfg.popSize,
        generations: this.cfg.generations,
        cxProb: this.cfg.cxProb,
        mutProb: this.cfg.mutProb,
        crossoverChildren: this.cfg.crossoverChildren,
        immigration: this.cfg.immigration,
        tournament: this.cfg.tournament,
        stall: this.cfg.stall,
        targetFitness: this.cfg.targetFitness,
        maxWallMs: this.cfg.maxWallMs,
        maxEvaluations: this.cfg.maxEvaluations,
        elitesPerGen: this.cfg.elite,
      }),
    }
  }

  // (Intentionally no async iterator here to keep footprint minimal.)

  /**
   * Run unit tests defined in the problem to validate the fitness function.
   * Returns an array of test results.
   */
  runUnitTests(): Array<{
    name: string
    genome: T
    fitness: number
    passed: boolean
    error?: string
  }> {
    if (!this.problem.unitTests || this.problem.unitTests.length === 0) {
      return []
    }

    return this.problem.unitTests.map((test) => {
      try {
        const fitness = this.safeFitness(test.genome)

        const result = test.test(fitness)
        const passed = result === true
        const error = typeof result === "string" ? result : undefined

        return {
          name: test.name,
          genome: test.genome,
          fitness,
          passed,
          error,
        }
      } catch (e) {
        return {
          name: test.name,
          genome: test.genome,
          fitness: -Infinity,
          passed: false,
          error: `Fitness evaluation failed: ${e instanceof Error ? e.message : String(e)}`,
        }
      }
    })
  }
}
